import { useId } from 'react'
import { observer } from 'mobx-react-lite'
import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { FormContainer } from '@/components/FormContainer'
import { Button } from '@/components/Button'
import { ButtonGroup } from '@/components/ButtonGroup'
import { SelectContainer } from '@/components/inputs/SelectContainer'
import type { AddRentalDialogViewModel } from './AddRentalDialogViewModel'
import { MandatoryFieldsSubtitle } from '../../../components/MandatoryFieldsSubtitle'
import { FormFieldRequiredLabel } from '../../../components/FormFieldRequiredLabel'
import { RENTAL_PROPERTY_TYPES } from '../../../data/rental-property-types'
import { AddressFieldSet } from '../../../components/AddressFieldSet'

interface AddRentalDialogProps {
  vm: AddRentalDialogViewModel
  testId?: string
}

export const AddRentalDialog = observer(function AddRentalDialog({
  vm,
  testId,
}: AddRentalDialogProps) {
  const formId = useId()

  return (
    <ModalDialog
      isOpen={vm.modal.showing}
      onClose={vm.closeDialog}
      isDismissable
      sheetSizing="stretch"
      isUsingDialogScrolling
      size="lg"
      testId={testId}
    >
      <ModalDialogHeader>
        <ModalDialogTitle>Add rental property</ModalDialogTitle>
        <ModalDialogDescription>
          <MandatoryFieldsSubtitle />
        </ModalDialogDescription>
      </ModalDialogHeader>

      <ModalDialogContent>
        <FormContainer
          id={formId}
          onSubmit={vm.save}
          testId={testId ? `${testId}-form` : undefined}
        >
          <SelectContainer
            model={vm}
            field="propertyType"
            label={<FormFieldRequiredLabel label="Property Type" />
            }
            items={RENTAL_PROPERTY_TYPES}
            getKey={([code]) => code}
            getLabel={([, label]) => label}
            required
            testId={testId ? `${testId}-property-type-select` : undefined}
          />
        </FormContainer>

        <AddressFieldSet
          model={vm.address}
          legend="Property Address"
          testId={testId ? `${testId}-address-field-set` : undefined}
        />
      </ModalDialogContent>

      <ModalDialogFooter sticky bordered compact>
        <ModalDialogActions>
          <ButtonGroup stretch>
            <Button
              variant="outline"
              label="Cancel"
              onPress={vm.closeDialog}
              testId={testId ? `${testId}-cancel-button` : undefined}
            />
            <Button
              variant="default"
              label="Add rental"
              type="submit"
              form={formId}
              testId={testId ? `${testId}-save-button` : undefined}
            />
          </ButtonGroup>
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
})
